import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import { toast } from 'sonner';
import { Users, Briefcase, Calendar, Sparkles, Building2, Crown, Check } from 'lucide-react';
import FeaturedBusinesses from '@/components/FeaturedBusinesses';
import PartnerLogos from '@/components/PartnerLogos';
import SpotlightArticles from '@/components/SpotlightArticles';
import { useInView } from 'react-intersection-observer';
import ScrollAnimation from '@/components/ScrollAnimation';
import { useLocation, useNavigate } from 'react-router-dom';

const Landing = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [showAuth, setShowAuth] = useState(false);
  const [isLogin, setIsLogin] = useState(true);
  const { login } = React.useContext(AuthContext);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: ''
  });
  const [currentTestimonial, setCurrentTestimonial] = useState(0);
  const [stats, setStats] = useState({ members: 0, industries: 0, events: 0 });
  const [realStats, setRealStats] = useState({ members: 0, industries: 0, events: 0 });
  const [hasAnimated, setHasAnimated] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotEmail, setForgotEmail] = useState('');
  const [isSubmittingForgot, setIsSubmittingForgot] = useState(false);

  // Check URL for auth trigger
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('auth') === 'signup') {
      setShowAuth(true);
      setIsLogin(false);
    } else if (params.get('auth') === 'login') {
      setShowAuth(true);
      setIsLogin(true);
    }
  }, [location]);

  const handleCancelAuth = () => {
    // Clear form
    setFormData({
      email: '',
      password: '',
      full_name: ''
    });
    // Switch to login tab
    setIsLogin(true);
    // Close modal
    setShowAuth(false);
  };

  const [testimonials, setTestimonials] = useState([
    {
      quote: "This platform connected me with incredible women who became both clients and mentors. My business grew 200% in just one year.",
      author: "Sarah Mitchell",
      role: "Tech Consultant"
    },
    {
      quote: "A supportive community that truly understands the challenges women face in business. The opportunities board helped me land my biggest client.",
      author: "Rachel Kim",
      role: "Marketing Strategist"
    }
  ]);

  // Fetch real stats from database
  useEffect(() => {
    const fetchRealStats = async () => {
      try {
        const response = await axios.get(`${API}/public/stats`);
        
        setRealStats({
          members: response.data.members || 0,
          industries: response.data.industries || 0,
          events: response.data.events || 0
        });
      } catch (error) {
        console.error('Failed to fetch real stats:', error);
        // Fallback to showing at least some data if API fails
        setRealStats({ members: 0, industries: 0, events: 0 });
      }
    };

    const fetchTestimonials = async () => {
      try {
        const response = await axios.get(`${API}/testimonials`);
        if (response.data && response.data.length > 0) {
          setTestimonials(response.data);
        }
      } catch (error) {
        console.error('Failed to fetch testimonials:', error);
        // Keep default testimonials if API fails
      }
    };

    fetchRealStats();
    fetchTestimonials();
  }, []);

  // Rotate testimonials
  useEffect(() => {
    if (testimonials.length === 0) return;
    const interval = setInterval(() => {
      setCurrentTestimonial((prev) => (prev + 1) % testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [testimonials.length]);

  // Animated counter that triggers on scroll into view
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated && realStats.members > 0) {
            setHasAnimated(true);
            
            const duration = 2000;
            const steps = 50;
            const interval = duration / steps;
            let step = 0;

            const timer = setInterval(() => {
              step++;
              const progress = step / steps;
              setStats({
                members: Math.floor(realStats.members * progress),
                industries: Math.floor(realStats.industries * progress),
                events: Math.floor(realStats.events * progress)
              });

              if (step >= steps) {
                clearInterval(timer);
                setStats(realStats);
              }
            }, interval);
          }
        });
      },
      { threshold: 0.3 }
    );

    const statsElement = document.querySelector('[data-testid="stats-section"]');
    if (statsElement) {
      observer.observe(statsElement);
    }

    return () => {
      if (statsElement) {
        observer.unobserve(statsElement);
      }
    };
  }, [realStats, hasAnimated]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsSubmittingForgot(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      toast.success('📧 Check your email! We\'ve sent you a password reset link.');
      setShowForgotPassword(false);
      setForgotEmail('');
    } catch (error) {
      if (error.response?.data?.detail?.includes('social login')) {
        toast.error('This account uses social login. Please sign in with your social provider.');
      } else {
        toast.success('📧 If that email exists, a password reset link has been sent.');
      }
    } finally {
      setIsSubmittingForgot(false);
    }
  };

  const handleSocialLogin = (provider) => {
    // Redirect to OAuth endpoint
    window.location.href = `${API}/auth/${provider}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const endpoint = isLogin ? '/auth/login' : '/auth/register';
      const payload = isLogin 
        ? { email: formData.email, password: formData.password }
        : formData;
      
      const response = await axios.post(`${API}${endpoint}`, payload);
      
      // Show verification message if registering
      if (!isLogin && response.data.message) {
        toast.success(response.data.message);
        toast.info('📧 Check your email to verify your account before logging in.');
      } else {
        toast.success(isLogin ? '✨ Welcome back! Great to see you again.' : '🎉 Welcome to Her Enterprise Hub!');
      }
      
      // Only login and navigate if it's a login (not registration)
      if (isLogin) {
        login(response.data.token, response.data.user);
        
        // Handle redirect parameter
        const params = new URLSearchParams(location.search);
        const redirectTo = params.get('redirect');
        if (redirectTo) {
          navigate(`/${redirectTo}`);
        } else {
          navigate('/dashboard');
        }
      } else {
        // After registration, switch to login tab
        setIsLogin(true);
      }
    } catch (error) {
      // Friendly, branded error messages
      const errorMsg = error.response?.data?.detail;
      
      if (isLogin) {
        // Email verification check
        if (errorMsg?.includes('verify your email') || errorMsg?.includes('email address')) {
          toast.error('📧 Please verify your email first!', {
            description: 'Check your inbox for the verification link.',
            action: {
              label: 'Resend Email',
              onClick: async () => {
                try {
                  await axios.post(`${API}/auth/resend-verification`, { email: formData.email });
                  toast.success('Verification email sent! Check your inbox.');
                } catch (err) {
                  toast.error('Failed to resend email. Please try again.');
                }
              }
            }
          });
        } else if (errorMsg?.includes('password') || errorMsg?.includes('credentials')) {
          toast.error('Oops! That didn\'t match. Try again or reset your password.');
        } else if (errorMsg?.includes('not found') || errorMsg?.includes('email')) {
          toast.error('We couldn\'t find an account with that email. Ready to join us?');
        } else {
          toast.error('Something went wrong. Please try again in a moment.');
        }
      } else {
        if (errorMsg?.includes('already exists') || errorMsg?.includes('email')) {
          toast.error('Looks like you already have an account! Try logging in instead.');
        } else {
          toast.error('We couldn\'t create your account right now. Please try again.');
        }
      }
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  if (showAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 50%, #D4E7D9 100%)' }}>
        {/* Background decorative elements */}
        <div className="absolute top-20 left-10 w-32 h-32 bg-rose-200 rounded-full opacity-10 blur-3xl"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-pink-200 rounded-full opacity-10 blur-3xl"></div>
        
        <div className="grid md:grid-cols-2 gap-8 max-w-5xl w-full items-center animate-fade-in">
          {/* Left side - Motivational content */}
          <div className="hidden md:block text-center md:text-left space-y-6 p-8">
            <div className="inline-block mb-4">
              <img src="/logo.png" alt="Her Enterprise Hub" className="h-24 w-auto object-contain" style={{ maxWidth: '300px' }} />
            </div>
            <h2 className="text-4xl font-bold leading-tight" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
              Where women build, collaborate, and lead together
            </h2>
            <p className="text-lg text-gray-700">
              Your next opportunity, connection, and collaboration starts here.
            </p>
            <div className="space-y-3 pt-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-rose-600" />
                </div>
                <p className="text-gray-600">Connect with ambitious women entrepreneurs</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <Briefcase className="w-4 h-4 text-rose-600" />
                </div>
                <p className="text-gray-600">Showcase your business to the community</p>
              </div>
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-rose-100 flex items-center justify-center flex-shrink-0">
                  <Calendar className="w-4 h-4 text-rose-600" />
                </div>
                <p className="text-gray-600">Attend exclusive networking events</p>
              </div>
            </div>
          </div>

          {/* Right side - Auth card */}
          <Card className="w-full shadow-2xl border-0 animate-slide-in-right" style={{ borderRadius: '1.5rem' }} data-testid="auth-card">
            <CardHeader className="text-center pb-4">
              <CardTitle className="text-3xl" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                Her Enterprise Hub
              </CardTitle>
              <CardDescription className="text-center text-base">
                Join our community of empowered women
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Tabs value={isLogin ? 'login' : 'register'} onValueChange={(v) => setIsLogin(v === 'login')}>
                <TabsList className="grid w-full grid-cols-2 mb-6">
                  <TabsTrigger value="login" data-testid="login-tab">Login</TabsTrigger>
                  <TabsTrigger value="register" data-testid="register-tab">Register</TabsTrigger>
                </TabsList>
                
                <TabsContent value="login" className="space-y-4">
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
                    <Input 
                      name="email" 
                      type="email" 
                      placeholder="Email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required
                      data-testid="login-email-input"
                      className="h-12"
                    />
                    <div className="space-y-2">
                      <Input 
                        name="password" 
                        type="password" 
                        placeholder="Password" 
                        value={formData.password} 
                        onChange={handleChange} 
                        required
                        data-testid="login-password-input"
                        className="h-12"
                      />
                      <div className="text-right">
                        <button 
                          type="button" 
                          className="text-sm text-gray-500 hover:text-rose-600 transition-colors"
                          onClick={() => setShowForgotPassword(true)}
                        >
                          Forgot password?
                        </button>
                      </div>
                    </div>
                    <Button type="submit" className="w-full h-12 bg-rose-500 hover:bg-rose-600 text-base font-semibold" data-testid="login-submit-button">
                      Login
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full h-12 text-base"
                      onClick={handleCancelAuth}
                    >
                      Cancel
                    </Button>
                  </form>
                  
                  {/* Social sign-in placeholder */}
                  <div className="mt-6">
                    <div className="relative">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-gray-200"></div>
                      </div>
                      <div className="relative flex justify-center text-sm">
                        <span className="px-2 bg-white text-gray-500">Or continue with</span>
                      </div>
                    </div>
                    <div className="mt-4 grid grid-cols-2 gap-3">
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="h-11"
                        onClick={() => handleSocialLogin('linkedin')}
                      >
                        <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                        </svg>
                        LinkedIn
                      </Button>
                      <Button 
                        type="button" 
                        variant="outline" 
                        className="h-11"
                        onClick={() => handleSocialLogin('google')}
                      >
                        <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                          <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                          <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                          <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                          <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                        </svg>
                        Google
                      </Button>
                    </div>
                  </div>
                </TabsContent>
                
                <TabsContent value="register" className="space-y-4">
                  <p className="text-sm text-center text-gray-600 bg-rose-50 p-3 rounded-lg mb-4">
                    It's free to join. Become part of a growing network of women in business
                  </p>
                  <form onSubmit={handleSubmit} className="space-y-4" data-testid="register-form">
                    <Input 
                      name="full_name" 
                      placeholder="Full Name" 
                      value={formData.full_name} 
                      onChange={handleChange} 
                      required
                      data-testid="register-name-input"
                      className="h-12"
                    />
                    <Input 
                      name="email" 
                      type="email" 
                      placeholder="Email" 
                      value={formData.email} 
                      onChange={handleChange} 
                      required
                      data-testid="register-email-input"
                      className="h-12"
                    />
                    <Input 
                      name="password" 
                      type="password" 
                      placeholder="Password (min. 12 characters)" 
                      value={formData.password} 
                      onChange={handleChange} 
                      required
                      minLength="12"
                      data-testid="register-password-input"
                      className="h-12"
                    />
                    <p className="text-xs text-center text-gray-500 py-2">
                      🎯 Add your business details later from your profile
                    </p>
                    <Button type="submit" className="w-full h-12 bg-rose-500 hover:bg-rose-600 text-base font-semibold" data-testid="register-submit-button">
                      Create Account
                    </Button>
                    <Button 
                      type="button" 
                      variant="outline" 
                      className="w-full h-12 text-base"
                      onClick={handleCancelAuth}
                    >
                      Cancel
                    </Button>
                  </form>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>
        </div>
        
        {/* Forgot Password Modal */}
        {showForgotPassword && (
          <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50 animate-fade-in" onClick={() => setShowForgotPassword(false)}>
            <Card className="w-full max-w-md shadow-2xl animate-scale-in" onClick={(e) => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-2xl text-rose-600">Reset Your Password</CardTitle>
                <CardDescription>
                  Enter your email address and we'll send you a link to reset your password.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleForgotPassword} className="space-y-4">
                  <Input
                    type="email"
                    placeholder="Your email address"
                    value={forgotEmail}
                    onChange={(e) => setForgotEmail(e.target.value)}
                    required
                    className="h-12"
                  />
                  <div className="flex gap-3">
                    <Button 
                      type="button" 
                      variant="outline" 
                      onClick={() => setShowForgotPassword(false)}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                    <Button 
                      type="submit" 
                      className="flex-1 bg-rose-500 hover:bg-rose-600"
                      disabled={isSubmittingForgot}
                    >
                      {isSubmittingForgot ? 'Sending...' : 'Send Reset Link'}
                    </Button>
                  </div>
                </form>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'linear-gradient(135deg, #FFE5EC 0%, #FFF8F3 50%, #D4E7D9 100%)' }}>
      {/* Header Navigation */}
      <header className="container mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <img src="/logo.png" alt="Her Enterprise Hub" className="h-12 w-auto object-contain" style={{ maxWidth: '250px' }} />
          <Button 
            onClick={() => setShowAuth(true)}
            variant="outline"
            className="border-rose-600 text-rose-600 hover:bg-rose-50"
          >
            Sign In
          </Button>
        </div>
      </header>

      {/* Hero Section */}
      <section className="container mx-auto px-4 pt-12 pb-32 relative" data-testid="hero-section">
        {/* Floating decorative elements */}
        <div className="absolute top-20 left-10 w-20 h-20 bg-rose-200 rounded-full opacity-20 animate-float"></div>
        <div className="absolute top-40 right-20 w-32 h-32 bg-pink-200 rounded-full opacity-20 animate-float" style={{ animationDelay: '1s' }}></div>
        <div className="absolute bottom-20 left-1/4 w-24 h-24 bg-rose-300 rounded-full opacity-20 animate-float" style={{ animationDelay: '2s' }}></div>
        
        <div className="text-center max-w-4xl mx-auto animate-fade-in-up relative z-10">
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold mb-4 animate-gradient" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Empowering Women in Business
          </h1>
          <p className="text-2xl sm:text-3xl mb-6 animate-slide-in-right" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#9f1239', fontWeight: '500' }}>
            Where women build, collaborate, and lead together.
          </p>
          <p className="text-lg sm:text-xl text-gray-700 mb-8 leading-relaxed">
            Connect with ambitious women entrepreneurs, showcase your business, discover opportunities, and grow together in a supportive community built by women, for women.
          </p>
          <div className="flex flex-col items-center gap-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                onClick={() => setShowAuth(true)} 
                className="bg-rose-600 hover:bg-rose-700 text-white px-8 py-6 text-lg rounded-full transition-all hover:scale-110 hover:shadow-2xl transform"
                data-testid="get-started-button"
              >
                Join Our Community
              </Button>
              <Button 
                onClick={() => setShowAuth(true)} 
                variant="outline"
                className="border-2 border-rose-600 text-rose-600 hover:bg-rose-50 px-8 py-6 text-lg rounded-full transition-all hover:scale-105"
                data-testid="share-story-button"
              >
                Share Your Story
              </Button>
            </div>
            <p className="text-sm text-gray-600">Free to sign up. Start connecting today.</p>
          </div>
        </div>
      </section>

      {/* Featured Businesses Carousel */}
      <FeaturedBusinesses />

      {/* Spotlight Articles */}
      <div className="container mx-auto px-4">
        <SpotlightArticles />
      </div>

      {/* Decorative Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
          <Sparkles className="w-6 h-6 text-rose-400" />
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
        </div>
      </div>

      {/* Features Section */}
      <section className="container mx-auto px-4 pb-20" data-testid="features-section">
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          <ScrollAnimation animation="fade-in-view" delay={0}>
            <Card className="glass hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer group h-full" data-testid="feature-network">
              <CardHeader>
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-colors">
                  <Users className="w-10 h-10 text-rose-600 group-hover:scale-110 transition-transform" />
                </div>
                <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Network</CardTitle>
                <CardDescription className="text-gray-600">Connect with like-minded women entrepreneurs and build lasting partnerships.</CardDescription>
              </CardHeader>
            </Card>
          </ScrollAnimation>
          
          <ScrollAnimation animation="fade-in-view" delay={100}>
            <Card className="glass hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer group h-full" data-testid="feature-showcase">
              <CardHeader>
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-colors">
                  <Briefcase className="w-10 h-10 text-rose-600 group-hover:scale-110 transition-transform" />
                </div>
                <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Showcase</CardTitle>
                <CardDescription className="text-gray-600">Promote your business, products, and success stories to the community.</CardDescription>
              </CardHeader>
            </Card>
          </ScrollAnimation>
          
          <ScrollAnimation animation="fade-in-view" delay={200}>
            <Card className="glass hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer group h-full" data-testid="feature-events">
              <CardHeader>
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-colors">
                  <Calendar className="w-10 h-10 text-rose-600 group-hover:scale-110 transition-transform" />
                </div>
                <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Events</CardTitle>
                <CardDescription className="text-gray-600">Attend workshops and exclusive events designed to help women in business grow.</CardDescription>
              </CardHeader>
            </Card>
          </ScrollAnimation>
          
          <ScrollAnimation animation="fade-in-view" delay={300}>
            <Card className="glass hover:shadow-2xl transition-all duration-300 hover:scale-105 hover:-translate-y-2 cursor-pointer group h-full" data-testid="feature-opportunities">
              <CardHeader>
                <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mb-4 group-hover:bg-rose-200 transition-colors">
                  <Sparkles className="w-10 h-10 text-rose-600 group-hover:scale-110 transition-transform" />
                </div>
                <CardTitle className="text-xl mb-2" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Opportunities</CardTitle>
                <CardDescription className="text-gray-600">Discover jobs, collaborations, and investment opportunities.</CardDescription>
              </CardHeader>
            </Card>
          </ScrollAnimation>
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
          <Sparkles className="w-6 h-6 text-rose-400" />
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
        </div>
      </div>

      {/* Stats Bar */}
      <section className="bg-white/60 backdrop-blur-sm py-12" data-testid="stats-section">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto text-center mb-8">
            <div className="animate-fade-in-up" data-testid="stat-members">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                {stats.members}{stats.members > 0 ? '+' : ''}
              </div>
              <div className="text-gray-700 font-medium">Women Entrepreneurs</div>
            </div>
            <div className="animate-fade-in-up" data-testid="stat-industries">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                {stats.industries}{stats.industries > 0 ? '+' : ''}
              </div>
              <div className="text-gray-700 font-medium">Industries Represented</div>
            </div>
            <div className="animate-fade-in-up" data-testid="stat-events">
              <div className="text-5xl font-bold mb-2" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
                {stats.events}{stats.events > 0 ? '+' : ''}
              </div>
              <div className="text-gray-700 font-medium">Events Hosted</div>
            </div>
          </div>
          
          {/* Mission Line - Emotional Cohesion */}
          <div className="text-center max-w-2xl mx-auto">
            <p className="text-lg italic text-gray-600" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
              "Together, we're building more than businesses. We're building a legacy of women supporting women."
            </p>
          </div>
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
          <Users className="w-6 h-6 text-rose-400" />
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
          <Calendar className="w-6 h-6 text-rose-400" />
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
        </div>
      </div>

      {/* Testimonials Section - Rotating Carousel */}
      <section className="container mx-auto px-4 py-20" data-testid="testimonials-section">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold mb-4" style={{ fontFamily: 'Cormorant Garamond, serif', color: '#881337' }}>
            Success Stories from Our Community
          </h2>
          <p className="text-gray-600">Hear from women who are building their businesses with us</p>
        </div>
        <div className="max-w-4xl mx-auto">
          {testimonials.length > 0 ? (
            <Card className="glass overflow-hidden">
              <CardContent className="pt-12 pb-8 px-12">
                <div className="text-center">
                  <div className="mb-6 text-rose-500 text-6xl">"</div>
                  <p className="text-xl text-gray-700 mb-8 italic leading-relaxed min-h-24">
                    {testimonials[currentTestimonial]?.quote || ''}
                  </p>
                  <div className="flex items-center justify-center gap-4 mb-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-rose-400 to-rose-600 rounded-full flex items-center justify-center text-white font-bold text-2xl">
                      {testimonials[currentTestimonial]?.author?.charAt(0) || 'H'}
                    </div>
                    <div className="text-left">
                      <div className="font-semibold text-lg">{testimonials[currentTestimonial]?.author || 'Member'}</div>
                      <div className="text-sm text-gray-600">{testimonials[currentTestimonial]?.role || 'Business Owner'}</div>
                    </div>
                  </div>
                  <div className="flex justify-center gap-2">
                    {testimonials.map((_, index) => (
                      <button
                        key={index}
                        onClick={() => setCurrentTestimonial(index)}
                        className={`w-2 h-2 rounded-full transition-all ${
                          index === currentTestimonial 
                            ? 'bg-rose-500 w-8' 
                            : 'bg-rose-200 hover:bg-rose-300'
                        }`}
                        aria-label={`Go to testimonial ${index + 1}`}
                    />
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
          ) : (
            <Card className="glass">
              <CardContent className="py-12 text-center">
                <p className="text-gray-500">Loading testimonials...</p>
              </CardContent>
            </Card>
          )}
        </div>
      </section>

      {/* Decorative Divider */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex items-center justify-center gap-4">
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
          <Briefcase className="w-6 h-6 text-rose-400" />
          <div className="h-px bg-gradient-to-r from-transparent via-rose-300 to-transparent flex-1 max-w-xs"></div>
        </div>
      </div>

      {/* Partners Section */}
      <PartnerLogos />

      {/* Footer */}
      <footer className="container mx-auto px-4 py-12 mt-16 border-t border-rose-200">
        <div className="max-w-6xl mx-auto">
          {/* Sustainability Banner */}
          <div className="glass p-6 rounded-lg mb-8 border border-green-100 bg-gradient-to-r from-green-50 to-emerald-50">
            <div className="flex flex-col md:flex-row items-center justify-center gap-4 text-center md:text-left">
              <div className="flex items-center gap-3">
                <svg className="w-8 h-8 text-green-600" viewBox="0 0 60 25" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M59.64 14.28h-8.06c.19 1.93 1.6 2.55 3.2 2.55 1.64 0 2.96-.37 4.05-.95v3.32a8.33 8.33 0 0 1-4.56 1.1c-4.01 0-6.83-2.5-6.83-7.48 0-4.19 2.39-7.52 6.3-7.52 3.92 0 5.96 3.28 5.96 7.5 0 .4-.04 1.26-.06 1.48zm-5.92-5.62c-1.03 0-2.17.73-2.17 2.58h4.25c0-1.85-1.07-2.58-2.08-2.58zM40.95 20.3c-1.44 0-2.32-.6-2.9-1.04l-.02 4.63-4.12.87V5.57h3.76l.08 1.02a4.7 4.7 0 0 1 3.23-1.29c2.9 0 5.62 2.6 5.62 7.4 0 5.23-2.7 7.6-5.65 7.6zM40 8.95c-.95 0-1.54.34-1.97.81l.02 6.12c.4.44.98.78 1.95.78 1.52 0 2.54-1.65 2.54-3.87 0-2.15-1.04-3.84-2.54-3.84zM28.24 5.57h4.13v14.44h-4.13V5.57zm0-4.7L32.37 0v3.36l-4.13.88V.88zm-4.32 9.35v9.79H19.8V5.57h3.7l.12 1.22c1-1.77 3.07-1.41 3.62-1.22v3.79c-.52-.17-2.29-.43-3.32.86zm-8.55 4.72c0 2.43 2.6 1.68 3.12 1.46v3.36c-.55.3-1.54.54-2.89.54a4.15 4.15 0 0 1-4.27-4.24l.01-13.17 4.02-.86v3.54h3.14V9.1h-3.13v5.85zm-4.91.7c0 2.97-2.31 4.66-5.73 4.66a11.2 11.2 0 0 1-4.46-.93v-3.93c1.38.75 3.1 1.31 4.46 1.31.92 0 1.53-.24 1.53-1C6.26 13.77 0 14.51 0 9.95 0 7.04 2.28 5.3 5.62 5.3c1.36 0 2.72.2 4.09.75v3.88a9.23 9.23 0 0 0-4.1-1.06c-.86 0-1.44.25-1.44.93 0 1.98 6.22 1.24 6.22 5.83z" fill="currentColor"></path>
                </svg>
                <div className="h-8 w-px bg-green-300"></div>
                <span className="text-green-700 font-semibold text-sm">Climate</span>
              </div>
              <p className="text-gray-700 text-sm leading-relaxed max-w-2xl">
                Empowering women also means protecting our planet. That's why we partner with Stripe Climate to support meaningful carbon removal projects, helping create a cleaner, more sustainable world for generations to come.
              </p>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="text-center md:text-left">
              <img src="/logo.png" alt="Her Enterprise Hub" className="h-20 w-auto mx-auto md:mx-0 mb-4 object-contain" style={{ maxWidth: '280px' }} />
              <p className="text-gray-600 max-w-md mb-4">
                Where women build, collaborate, and lead together.
              </p>
              <div className="flex flex-col gap-2">
                <a href="mailto:info@herenterprisehub.com" className="text-rose-600 hover:text-rose-700 text-sm font-semibold">
                  info@herenterprisehub.com
                </a>
                <a href="/contact" className="text-rose-600 hover:text-rose-700 text-sm font-semibold">
                  Contact Us
                </a>
                <a href="/partner" className="text-rose-600 hover:text-rose-700 text-sm font-semibold">
                  Partner With Us
                </a>
              </div>
            </div>
            <div className="text-center md:text-right">
              <p className="text-gray-600 mb-4">© 2024 Her Enterprise Hub</p>
              <p className="text-sm text-gray-500 mb-3">Empowering Women in Business</p>
              <div className="flex flex-col gap-1">
                <a href="/user-guide" className="text-sm text-gray-600 hover:text-rose-600" data-testid="landing-footer-user-guide-link">User Guide</a>
                <a href="/contact" className="text-sm text-gray-600 hover:text-rose-600">Contact</a>
                <a href="/partner" className="text-sm text-gray-600 hover:text-rose-600">Partner</a>
                <a href="/impressum" className="text-sm text-gray-600 hover:text-rose-600" data-testid="landing-footer-impressum-link">Impressum</a>
                <a href="/datenschutz" className="text-sm text-gray-600 hover:text-rose-600" data-testid="landing-footer-datenschutz-link">Datenschutz</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;